/**
 * FlagContext — React context for feature flag resolution.
 *
 * Fetches resolved flags from /config/flags (public, unauthenticated proxy
 * on the bacchus worker). Falls back to build-time defaults if the fetch
 * fails (progressive enhancement — works without config worker deployed).
 *
 * @module contexts/FlagContext
 */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

/**
 * Build-time defaults — mirrors platform/config/src/flags/registry.ts.
 * Duplicated here to avoid cross-workspace imports (rootDir boundary).
 * When adding flags to the registry, update this object too.
 */
const BUILD_TIME_DEFAULTS: Record<string, boolean> = {
  'commercial.var_dashboard': true,
  'commercial.portfolio_analytics': true,
  'commercial.ai_sidebar': false,
  'commercial.multi_peril': false,
  'technical.v3_design': false,
  'technical.search_overlay': false,
  'technical.panel_toggles': false,
  'technical.historical_replay': true,
  'compliance.gdpr_retention': false,
  'compliance.ca_wildfire_disclosure': false,
  'compliance.uk_approved_document_b': false,
  'compliance.ibhs_gold': false,
};

interface FlagState {
  flags: Record<string, boolean>;
  loaded: boolean;
}

// Use null as the default so consumers can detect missing provider.
// A missing FlagProvider is a bug (infinite spinner on FlagGate), not
// a graceful fallback — the useFlag/useFlagLoaded hooks throw in dev
// to surface it immediately. In production the hooks fall back to
// build-time defaults as a last resort.
const FlagContext = createContext<FlagState | null>(null);

// Derive flag URL from the API base URL.
// In production: VITE_API_BASE_URL = https://api.firebreakrisk.com/v1/bacchus
// → flags URL = https://api.firebreakrisk.com/v1/bacchus/config/flags
// In local dev: empty string → /config/flags (relative, proxied by Vite)
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
const CONFIG_FLAGS_URL = `${API_BASE}/config/flags`;

interface FlagResponse {
  flags: Record<string, boolean>;
}

export function FlagProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FlagState>({
    flags: BUILD_TIME_DEFAULTS,
    loaded: false,
  });

  useEffect(() => {
    fetch(CONFIG_FLAGS_URL)
      .then(r => r.ok ? r.json() as Promise<FlagResponse> : null)
      .catch(() => null)
      .then(data => {
        if (data?.flags) {
          // Merge with defaults — partial API response must not disable
          // flags that default to true (e.g. commercial.var_dashboard)
          setState({ flags: { ...BUILD_TIME_DEFAULTS, ...data.flags }, loaded: true });
        } else {
          // API unreachable — use build-time defaults, mark as loaded so
          // FlagGate doesn't spin forever (progressive enhancement)
          setState(prev => ({ ...prev, loaded: true }));
        }
      });
  }, []);

  return <FlagContext.Provider value={state}>{children}</FlagContext.Provider>;
}

function useFlagContextSafe(): FlagState {
  const ctx = useContext(FlagContext);
  if (ctx === null) {
    if (import.meta.env.DEV) {
      throw new Error(
        'useFlag/useFlagLoaded must be used inside <FlagProvider>. ' +
          'Wrap the component tree in <FlagProvider> (see src/frontend/index.tsx).',
      );
    }
    // Production graceful fallback — return build-time defaults as if
    // flags had loaded. Better than crashing the whole app tree in prod.
    return { flags: BUILD_TIME_DEFAULTS, loaded: true };
  }
  return ctx;
}

export function useFlag(key: string): boolean {
  const { flags } = useFlagContextSafe();
  return flags[key] ?? false;
}

/**
 * Returns whether the flag system has loaded from the API.
 * When false, useFlag returns build-time defaults and consumers
 * should fall back to env vars for version gating.
 */
export function useFlagLoaded(): boolean {
  const { loaded } = useFlagContextSafe();
  return loaded;
}
